import React, { useState } from "react";
import { Mail, LogIn, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useI18n } from "@/i18n/I18nContext";
import { translateApiError } from "@/utils/apiErrors";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/utils/analytics";
import { WIZARD } from "@/constants/testIds";

/**
 * Shown right where the login/signup wall used to force-redirect every anonymous visitor
 * (Itération 50 audit - a real drop-off point at the payment step). "Continuer avec mon
 * email" provisions a lightweight passwordless account (POST /auth/guest) behind the scenes,
 * so the rest of the order/payment flow works completely unchanged.
 */
export default function GuestCheckoutGate({ open, onLogin, onGuestReady, error, setError }) {
  const { t, language } = useI18n();
  const { guestCheckout } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleContinueAsGuest = async () => {
    if (!email.trim() || !email.includes("@")) {
      setError(t("wizard.guestGateEmailInvalid", "Merci d'indiquer un email valide."));
      return;
    }
    setLoading(true);
    setError("");
    try {
      await guestCheckout(email.trim());
      trackEvent("guest_checkout_started");
      onGuestReady();
    } catch (err) {
      setError(translateApiError(err?.response?.data?.detail, language) || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent data-testid={WIZARD.guestGateModal} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("wizard.guestGateTitle", "Comment souhaitez-vous continuer ?")}</DialogTitle>
          <DialogDescription>{t("wizard.guestGateDesc", "Aucun compte n'est requis pour payer - vous recevrez votre chanson par email.")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder={t("wizard.guestGateEmailPlaceholder", "Votre adresse email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid={WIZARD.guestGateEmailInput}
          />
          <Button
            className="w-full rounded-full"
            onClick={handleContinueAsGuest}
            disabled={loading}
            data-testid={WIZARD.guestGateContinueBtn}
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
            {t("wizard.guestGateContinueBtn", "Continuer avec mon email")}
          </Button>
          {error && <p className="text-sm font-medium text-destructive">{error}</p>}

          <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" />
            {t("auth.orDivider", "or")}
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button variant="outline" className="w-full rounded-full" onClick={onLogin} data-testid={WIZARD.guestGateLoginBtn}>
            <LogIn className="mr-2 h-4 w-4" />
            {t("wizard.guestGateLoginBtn", "Se connecter / créer un compte")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
